/**
 * Availability gate for the FactSet company ID lookup path.
 *
 * Older OAuth grants may not include `api|content/idlookup`. Probe only for a
 * run that can use direct lookup, attempt at most one reconnect per client
 * session, cache only a positive grant, and otherwise leave the pre-feature
 * research path unchanged.
 */

import { FACTSET_SCOPES } from "../shared/factset/factsetConstants";
import {
  getFactSetOAuthSession,
  type FactSetOAuthSessionData,
} from "../shared/factset/factsetOAuthClient";
import { forceReconnectFactSetOAuth } from "../shared/factset/factsetReconnect";
import { registerFactSetClientSessionClearHandler } from "../shared/factset/factsetSessionLifecycle";

export type IdLookupUnavailableReason =
  | "not_connected"
  | "scope_missing"
  | "probe_failed";

export interface IdLookupAvailability {
  available: boolean;
  reason?: IdLookupUnavailableReason;
  message?: string;
}

export interface ResolveIdLookupAvailabilityOptions {
  /** Optional logger for run logs. Tool paths pass none so they stay silent. */
  log?: (message: string) => void;
}

interface ReauthFlight {
  generation: number;
  promise: Promise<void>;
}

let sessionGeneration = 0;
let reauthAttempted = false;
let reauthFlight: ReauthFlight | null = null;
/** Only a granted result is cached; unavailable results remain retryable. */
let grantedAvailability: IdLookupAvailability | null = null;

function clearIdLookupSessionState(): void {
  sessionGeneration += 1;
  grantedAvailability = null;
  reauthAttempted = false;
  reauthFlight = null;
}

registerFactSetClientSessionClearHandler(clearIdLookupSessionState);

function sessionHasIdLookup(session: FactSetOAuthSessionData): boolean {
  return (
    session.hasIdLookupAccess === true ||
    (session.scopes ?? []).includes(FACTSET_SCOPES.ID_LOOKUP)
  );
}

/**
 * Run a single FactSet reconnect, or join the reconnect already in flight for
 * the current session generation. A stale completion cannot clear a newer
 * generation's flight.
 */
export function runOrJoinIdLookupReauth(): Promise<void> {
  const generation = sessionGeneration;
  if (reauthFlight?.generation === generation) {
    return reauthFlight.promise;
  }

  reauthAttempted = true;
  const promise = forceReconnectFactSetOAuth();
  const flight: ReauthFlight = {
    generation,
    promise: promise.finally(() => {
      if (
        sessionGeneration === generation &&
        reauthFlight?.generation === generation &&
        reauthFlight.promise === flight.promise
      ) {
        reauthFlight = null;
      }
    }),
  };
  reauthFlight = flight;
  return flight.promise;
}

async function resolveForGeneration(
  generation: number,
  options: ResolveIdLookupAvailabilityOptions,
): Promise<IdLookupAvailability> {
  const { log } = options;
  try {
    let session = await getFactSetOAuthSession();
    if (generation !== sessionGeneration) {
      return resolveIdLookupAvailability(options);
    }
    if (!session.connected) {
      log?.("FactSet OAuth is not connected; company lookup is unavailable.");
      return { available: false, reason: "not_connected" };
    }
    if (sessionHasIdLookup(session)) {
      const granted = { available: true } as const;
      if (generation === sessionGeneration) {
        grantedAvailability = granted;
      }
      return granted;
    }

    if (!reauthAttempted || reauthFlight?.generation === generation) {
      try {
        log?.("Reconnecting FactSet to grant company lookup access...");
        await runOrJoinIdLookupReauth();
        if (generation !== sessionGeneration) {
          return resolveIdLookupAvailability(options);
        }
        session = await getFactSetOAuthSession();
        if (generation !== sessionGeneration) {
          return resolveIdLookupAvailability(options);
        }
        if (session.connected && sessionHasIdLookup(session)) {
          log?.("FactSet reconnected with company lookup access.");
          const granted = { available: true } as const;
          if (generation === sessionGeneration) {
            grantedAvailability = granted;
          }
          return granted;
        }
        log?.(
          "FactSet reconnected, but the company lookup scope is still missing.",
        );
        return { available: false, reason: "scope_missing" };
      } catch (reauthError) {
        if (generation !== sessionGeneration) {
          return resolveIdLookupAvailability(options);
        }
        const message =
          reauthError instanceof Error ? reauthError.message : String(reauthError);
        log?.(`FactSet company lookup reconnect did not complete: ${message}`);
        return { available: false, reason: "scope_missing", message };
      }
    }

    log?.("FactSet OAuth is connected without the company lookup scope.");
    return { available: false, reason: "scope_missing" };
  } catch (error) {
    if (generation !== sessionGeneration) {
      return resolveIdLookupAvailability(options);
    }
    const message = error instanceof Error ? error.message : String(error);
    log?.(`Could not read the FactSet OAuth session for company lookup: ${message}`);
    return { available: false, reason: "probe_failed", message };
  }
}

/**
 * Resolve whether the signed-in user's FactSet grant can serve company
 * lookups, attempting at most one reconnect per client session.
 */
export async function resolveIdLookupAvailability(
  options: ResolveIdLookupAvailabilityOptions = {},
): Promise<IdLookupAvailability> {
  if (grantedAvailability) return grantedAvailability;
  return resolveForGeneration(sessionGeneration, options);
}

export function describeIdLookupUnavailable(
  availability: IdLookupAvailability,
): string {
  switch (availability.reason) {
    case "not_connected":
      return "FactSet is not connected, so FactSet company lookup is unavailable. Connect FactSet and try again.";
    case "scope_missing":
      return "This FactSet connection is not authorized for company lookup. Reconnect FactSet to grant ID Lookup access.";
    default:
      return `FactSet company lookup availability could not be confirmed${
        availability.message ? `: ${availability.message}` : "."
      }`;
  }
}
